import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import InputSelect from './InputSelect'
import UploadDesignEdit from '../../TagBrandEdit/components/UploadDesign'
import UploadDesign from './UploadDesign'
import { getListBrands, createTagBrand, editTagBrand, getTagBrandDetail } from '../../../services/api/apiBrands'
import { getListMaterials } from "../../../services/api/apiSku"
import { Spin, message } from 'antd'
import getHistory from "../../../getHistory"
import PropTypes from 'prop-types'

const BRAND_TYPE = [
    { label: 'Branding', value: 'branding' },
    { label: 'Packaging ', value: 'packaging' }
]

const initialErrors = {}

const TagBrandForm = ({ idTagBrand = null, children }) => {
    const isEdit = Boolean(idTagBrand)

    const [brandName, setBrandName] = useState('')
    const [brandType, setBrandType] = useState('')
    const [materialCode, setMaterialCode] = useState('')
    const [brandDetail, setBrandDetail] = useState('')
    const [fileUpload, setFileUpload] = useState('')
    const [linkDesign, setLinkDesign] = useState('')
    const [description, setDescription] = useState('')
    const [note, setNote] = useState('')
    const [quantity, setQuantity] = useState('')
    const [baseCost, setBaseCost] = useState('')
    const [fullFee, setFullFee] = useState('')
    const [errors, setErrors] = useState(initialErrors)
    const [loading, setLoading] = useState(false)
    const [errBrand, setErrBrand] = useState('')
    const [error, setError] = useState('')
    const [listBrands, setListBrands] = useState([])
    const [listMaterialCode, setListMaterialCode] = useState([])

    // safe delete helper for errors
    const clearError = key => {
        setErrors(prev => {
            if (!prev || typeof prev !== 'object') return {}
            const copy = { ...prev }
            delete copy[key]
            return copy
        })
    }

    const _handleChangeBrandType = type => {
        setBrandType(type)
        clearError('brandType')
    }

    const _handleChangeBrandName = name => {
        setBrandName(name)
        clearError('brandName')
    }

    const _handleChangeMaterialCode = code => {
        setMaterialCode(code)
        clearError('materialCode')
    }

    const _handleChangeBrandDetail = detail => {
        setBrandDetail(detail)
        clearError('brandDetail')
    }

    const _handleChangeFileUpload = file => {
        setFileUpload(file)
    }

    const _handleRemovePreview = () => {
        setFileUpload('')
    }

    const _handleChangeDescription = val => {
        setDescription(val)
    }

    const _handleChangeNote = txt => {
        setNote(txt)
    }

    const _handleChangeQuantity = quantity => {
        const rx_live = /^[+-]?\d+(\.\d+)?$/
        if (rx_live.test(quantity)) {
            setQuantity(quantity)
            delete errors.quantity
        }
    }

    const _handleChangeBaseCost = cost => {
        const rx_live = /^[+]?\d*(?:[.,]\d{0,4})?$/
        if (rx_live.test(cost)) {
            setBaseCost(cost)
            clearError('baseCost')
        }
    }

    const _handleChangeFullFee = cost => {
        const rx_live = /^[+]?\d*(?:[.,]\d{0,4})?$/
        if (rx_live.test(cost)) {
            setFullFee(cost)
            clearError('fullFee')
        }
    }

    const _fetchListBrands = async () => {
        try {
            const { data, message: msg, success } = await getListBrands({ limit: 1000 })
            const { Brands } = data || {}
            if (!success) return setErrBrand(msg)
            const ListBrands = (Brands || []).filter(b => b.is_active).map(item => ({
                value: item._id,
                label: item.name
            }))
            setListBrands(ListBrands)
        } catch (e) {
            // keep it non-blocking for UI
            setErrBrand(e.message || 'Fetch brands error')
        }
    }

    const _fetchMaterialCode = async () => {
        try {
            const payload = { page: 1, limit: 10000, object: "BrandTag" }
            const { data: res = {} } = await getListMaterials(payload)
            const { data, success, message: mess } = res
            if (!success) throw new Error(mess)
            const newData = (data.data || []).map(item => ({ value: item.name, label: item.name }))
            setListMaterialCode(newData)
        } catch (err) {
            message.error(err.message)
        }
    }

    const _fetchDetailTagBrands = async () => {
        if (!isEdit) return
        try {
            const { data } = await getTagBrandDetail(idTagBrand)
            const { data: res, success, message: mess } = data
            if (!success) return setError(mess)
            const { brand, design, fee, name, note, quantity, type, base_cost, material_code, description } = res
            setBrandName(brand?._id || '')
            setBrandType(type || '')
            setMaterialCode(material_code || '')
            setBrandDetail(name || '')
            setLinkDesign(design || '')
            setDescription(description || '')
            setNote(note || '')
            setQuantity(quantity || '')
            setBaseCost(base_cost || '')
            setFullFee(fee || '')
        } catch (e) {
            setError(e.message)
        }
    }

    useEffect(() => {
        _fetchListBrands()
        _fetchMaterialCode()
        _fetchDetailTagBrands()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idTagBrand])

    const _validate = () => {
        let err = {}
        if (!brandName) err.brandName = 'Brand is required!'
        if (!brandType) err.brandType = 'Brand type is required!'
        if (!materialCode) err.materialCode = 'Material code is required!'
        if (!brandDetail) err.brandDetail = 'Brand detail is required!'
        if (!quantity) err.quantity = 'Quantity is required!'
        setErrors(err)
        return Object.keys(err).length === 0
    }

    const handleSave = async () => {
        const isValidate = _validate()
        if (!isValidate) return

        setLoading(true)
        try {
            const payload = {
                brand: brandName,
                type: brandType,
                name: brandDetail.trim(),
                quantity: +quantity,
                base_cost: +baseCost || 0,
                fee: +fullFee || 0,
                is_active: true,
                material_code: materialCode ? materialCode.trim() : '',
            }

            // include optional fields for edit (note/description)
            if (note) payload.note = note.trim()
            if (description) payload.description = description.trim()

            const formData = new FormData()
            for (const key of Object.keys(payload)) formData.append(key, payload[key])
            if (fileUpload) formData.append('file', fileUpload)

            let apiResponse
            if (isEdit) {
                apiResponse = await editTagBrand(idTagBrand, formData)
            } else {
                apiResponse = await createTagBrand(formData)
            }

            const { data } = apiResponse
            const { success, message: mess } = data
            if (!success) return message.error(mess)

            if (isEdit) {
                message.success("Update tag brand success!!")
                // refresh detail after update
                _fetchDetailTagBrands()
            } else {
                // reset form & redirect to listing
                setBrandName('')
                setBrandType('')
                setMaterialCode('')
                setBrandDetail('')
                setFileUpload('')
                setDescription('')
                setNote('')
                setQuantity('')
                setBaseCost('')
                setFullFee('')

                const history = getHistory()
                history.push(`/tag-brands`)
                message.success("Create tag brand success!")
            }
        } catch (e) {
            message.error(e.message || 'Save failed')
        } finally {
            setLoading(false)
        }
    }

    if (error) return <span className='text-danger'>{error}</span>

    return (
        <div className={isEdit ? 'TagBrandEditPage' : 'TagBrandNewPage'}>
            <div className="ReturnPage mb-2">
                <Link to={'/tag-brands'} className="txt-16">
                    <i className="fa fa-chevron-left mr-2" /> List tag brands inventory
                </Link>
            </div>
            <div className="TagBrandForm">
                <div className='PageHeading d-flex align-items-center justify-content-between border-bottom'>
                    <h1 className="PageTitle font-weight-bold mb-0">{isEdit ? 'Edit tag brands' : 'Add news tag brands'}</h1>
                    <div className="RightTop">
                        <div className='Actions mb-2'>
                            <Link to={'/tag-brands'}>
                                <button className='btn btn-secondary mx-1 px-3' style={{ background: '#C4C4C4' }}>
                                    Cancel
                                </button>
                            </Link>
                            <button
                                className='btn btn-primary mx-1 px-4'
                                style={{ background: '#4A80F7' }}
                                onClick={handleSave}
                                disabled={loading}
                            >
                                <Spin spinning={loading} size="small" className='mr-2' />Save
                            </button>
                        </div>
                    </div>
                </div>

                <Spin spinning={loading} size="large" className='mr-2' tip='Loading...' >
                    <div className="SectionInner card px-4 py-4 mt-3" style={{ minHeight: '80vh' }}>
                        <div className="row">
                            <div className="col-12 mb-3">
                                <InputSelect
                                    title="Brand"
                                    options={listBrands}
                                    placeholder="---Choose brands---"
                                    handleChangeBrandInput={_handleChangeBrandName}
                                    type="select"
                                    errors={errors}
                                    keyErr="brandName"
                                    errBrand={errBrand}
                                    valueInput={brandName}
                                    disabled={isEdit}
                                />
                            </div>
                            <div className="col-12 mb-3">
                                <InputSelect
                                    title="Brand type"
                                    options={BRAND_TYPE}
                                    placeholder="---Choose brand type---"
                                    handleChangeBrandInput={_handleChangeBrandType}
                                    type="select"
                                    errors={errors}
                                    keyErr="brandType"
                                    valueInput={brandType}
                                />
                            </div>
                            <div className="col-12 mb-3">
                                <InputSelect
                                    title="Material code"
                                    options={listMaterialCode}
                                    placeholder="---Choose material code---"
                                    handleChangeBrandInput={_handleChangeMaterialCode}
                                    type="select"
                                    errors={errors}
                                    keyErr="materialCode"
                                    valueInput={materialCode}
                                />
                            </div>
                            <div className="col-12 mb-3">
                                <InputSelect
                                    title="Brand Detail"
                                    handleChangeBrandInput={_handleChangeBrandDetail}
                                    valueInput={brandDetail}
                                    type="input"
                                    errors={errors}
                                    keyErr="brandDetail"
                                />
                            </div>
                            <div className="col-12 mb-3">
                                {isEdit ?
                                    <UploadDesignEdit
                                        handleChangeFileUpload={_handleChangeFileUpload}
                                        handleChangeNote={_handleChangeNote}
                                        handleChangeDescription={_handleChangeDescription}
                                        note={note}
                                        description={description}
                                        fileUpload={fileUpload}
                                        handleRemovePreview={_handleRemovePreview}
                                        linkDesign={linkDesign}
                                    /> :
                                    <UploadDesign
                                        handleChangeFileUpload={_handleChangeFileUpload}
                                        handleChangeNote={_handleChangeNote}
                                        handleChangeDescription={_handleChangeDescription}
                                        note={note}
                                        description={description}
                                        fileUpload={fileUpload}
                                        handleRemovePreview={_handleRemovePreview}
                                        linkDesign={linkDesign}
                                    />
                                }
                            </div>
                            <div className="col-12 mb-3">
                                <InputSelect
                                    title="Quantity"
                                    handleChangeBrandInput={_handleChangeQuantity}
                                    valueInput={quantity}
                                    type="input"
                                    errors={errors}
                                    keyErr="quantity"
                                />
                            </div>
                            <div className="col-12 mb-3">
                                <InputSelect
                                    title="Base cost"
                                    handleChangeBrandInput={_handleChangeBaseCost}
                                    valueInput={baseCost}
                                    type="cost"
                                    errors={errors}
                                    keyErr="baseCost"
                                />
                            </div>
                            <div className="col-12">
                                <InputSelect
                                    title="Fullfillment fee"
                                    handleChangeBrandInput={_handleChangeFullFee}
                                    valueInput={fullFee}
                                    type="cost"
                                    errors={errors}
                                    keyErr="fullFee"
                                />
                            </div>
                        </div>
                    </div>

                    {/* children allow edit page to pass TagBrandHistories */}
                    {children}
                </Spin>
            </div>
        </div>
    )
}

TagBrandForm.propTypes = {
    idTagBrand: PropTypes.string,
    children: PropTypes.node
}

export default TagBrandForm
